import { useState, useRef, useEffect } from "react";
import {
  Tree,
  getDescendants,
  getBackendOptions,
  MultiBackend,
} from "@minoru/react-dnd-treeview";
import { DndProvider } from "react-dnd";
import { Tag, InlineEdit, Input, IconButton, Whisper, Tooltip, Button, Uploader, Popover, useToaster, Message, Modal } from "rsuite";

import TrashIcon from '@rsuite/icons/Trash';
import MenuIcon from '@rsuite/icons/Menu';
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine';
import AttachmentIcon from '@rsuite/icons/Attachment';
import EditIcon from '@rsuite/icons/Edit';

import CollaspedFillIcon from '@rsuite/icons/CollaspedFill';
import ExpandOutlineIcon from '@rsuite/icons/ExpandOutline';

import FolderFillIcon from '@rsuite/icons/FolderFill';
import PageIcon from '@rsuite/icons/Page';
import ThreeColumnsIcon from '@rsuite/icons/ThreeColumns';

import { updateBrief, updateBriefSection } from "../briefs/briefCreationManager";
import { useParams } from "react-router-dom";

const ITEM_TYPES = {
    section: {
        label: 'Раздел',
        color: 'green',
    },
    parameter: {
        label: 'Параметр',
        color: 'yellow',
    },
}
  
const renderTreeNode = (item,
                        onToggle,
                        depth,
                        isOpen, debounce, files, setFiles) => {
    return (
        <div className={`brief-list-item-wrapper`}>
        <div className={`brief-list-item-box view`} style={{marginLeft: 2 * depth + "rem"}}>
            <div style={{display: 'flex', alignItems: 'center', width: '100%', height: "fit-content", borderRadius: "0.5rem", backgroundColor: 'var(--rs-gray-50)'}}>
                <Tag className={"brief-tree-tag"} onClick={onToggle} style={{marginRight: 10}} color={ITEM_TYPES[item.data.type].color}>
                    <span className={`brief-tree-tag-label ${item.droppable ? "expandable" : ""}`}>
                      {ITEM_TYPES[item.data.type].label}</span>
                    {   
                        item.droppable &&
                        <ArrowDownLineIcon className={"brief-tree-tag-icon"} style={{transform: `rotate(${isOpen ? 0 : -90}deg)`}} />
                    }
                </Tag>
                {item.data.have_ocr ? <Whisper placement="top" trigger="hover" controlId="control-id-hover" speaker={<Tooltip>OCR</Tooltip>}><ThreeColumnsIcon /></Whisper> : null}
                <InlineEdit disabled defaultValue={item.text} onChange={(text) => {
                  }}
                  style={{width: '100%'}}>
                    <Input style={{width: '100%'}}/>
                </InlineEdit>
            </div>
            <div style={{marginLeft: '0.25rem', display: 'flex', alignItems: 'center'}}>
            </div>
        </div>
            {
              item.data.type == "parameter" && item.data.have_file &&
              <span style={{
                marginLeft: 2 * (depth + 1) + "rem",
                width: `calc(100% - ${2 * (depth + 1) + "rem"})`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between'}}>
                <Uploader
                  fileListVisible={false}
                  multiple
                  action={`${process.env.REACT_APP_API_HOST}/api/parameters/${item.id}/files`}
                  fileList={files[item.id] ? files[item.id] : []}
                  onChange={(e) => setFiles(prev => ({...prev, [item.id]: e}))}
                  defaultFileList={item.data.files ? item.data.files.map(file => ({name: file.filename, fileKey: file.id})) : []}
                >
                  <Button
                    startIcon={<AttachmentIcon />}
                    style={{marginRight: '0.75rem'}}
                    className={`brief-tree-option-icon ${item.data.have_file ? "active" : ""}`}
                    onClick={() => {

                    }}
                  >Прикрепить файлы</Button>
                </Uploader>
                { files[item.id] && files[item.id].length > 0 &&
                  <Whisper placement="top" trigger="hover" speaker={
                  <Popover className="rs-uploader-file-items">
                    {files[item.id] && files[item.id].map(file => (
                      <div className="rs-uploader-file-item">
                        <div className="rs-uploader-file-item-name">{file.name}</div>
                      </div>
                    ))}
                  </Popover>}>
                    <span className="uploader-files-label">Прикрепленные файлы: {files[item.id].length}</span>
                  </Whisper>
                  }
              </span>
            }
            { item.data.have_input &&
              <div className="brief-list-item-input" style={{marginLeft: 2 * (depth + 1) + "rem", width: `calc(100% - ${2 * (depth + 1) + "rem"})`}}>
                <Tag className="brief-list-item-input-label" color="orange">
                  Необходим ввод
                </Tag>
                <Input
                  onChange={(text) => debounce(item.id, () => updateBriefSection(item.id, item.data.type, {input_text: text}), 1000)}
                  className="brief-list-item-input-input"
                  defaultValue={item.data.input_text}/>
              </div>
            }
        </div>
    );
};

function App({ items, setItems }) {
  const ref = useRef(null);
  const { key } = useParams();
  const toast = useToaster();

  const [open, setOpen] = useState(false);
  const [timeOuts, setTimeouts] = useState({});
  const [files, setFiles] = useState(items.reduce((acc, item) => ({...acc, [item.id]: item.data.files?.map(
    file => ({name: file.filename, fileKey: file.id})
  )}), {}));

  useEffect(() => {
    setFiles(items.reduce((acc, item) => ({...acc, [item.id]: item.data.files?.map(
      file => ({name: file.filename, fileKey: file.id})
    )}), {}))
  }, [items])

  function debounce(item_id, func, delay) {
    if (timeOuts[item_id]) {
      clearTimeout(timeOuts[item_id]);
    }

    setTimeouts(prev => ({ ...prev, [item_id]: setTimeout(func, delay)}));
  }

  const handleCloseAll = () => ref.current?.closeAll();
  const handleOpenAll = () => ref.current?.openAll();


  return (
    <div className="brief-tree">
      <div className="brief-tree-header">
        <span>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Свернуть всё</Tooltip>}>
            <IconButton className="brief-tree-header-icon" onClick={handleCloseAll} icon={<CollaspedFillIcon />}/>
          </Whisper>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Развернуть всё</Tooltip>}>
          <IconButton className="brief-tree-header-icon" onClick={handleOpenAll} icon={<ArrowDownLineIcon />}/>
          </Whisper>
        </span>
        <Button size="sm" appearance="primary" color="green" onClick={() => {
          updateBrief(key, {status: "waiting"})
          setOpen(true)
        }}>Отправить</Button>
      </div>
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <Tree
          enableAnimateExpand={true}
          ref={ref}
          tree={items}
          rootId={0}
          classes={
            { 
              root: 'brief-tree-root',
              dropTarget: 'brief-tree-drop-target',
              draggingSource: 'draggingSource',
            }
          }
          insertDroppableFirst={false}
          initialOpen
          canDrag={() => false}
          sort={false}
          render={(node,
            { handleRef, depth, isOpen, onToggle }
          ) => renderTreeNode(
            node, onToggle,
            depth, isOpen, debounce, files, setFiles)}
        />
      </DndProvider>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Modal.Header>
          <Modal.Title>Бриф отправлен менеджеру</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message showIcon type="success">
            Отправлено на проверку
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <Button color="green" onClick={() => setOpen(false)} appearance="primary">
            Хорошо
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default App